import {AfterViewInit, Component, Inject, OnInit, TemplateRef, ViewChild, ViewContainerRef} from '@angular/core';
import {env, logoUrl} from '../../../../core/utils/utils';
import {AztecScannerService} from '../../../services/aztec-scanner.service';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material';
import {CalculationService} from '../../../../core/services/impl/calculation.service';
import {
  VehicleModelStateService
} from '../../../../core/services/impl/section-services/vehicle-model/vehicle-model-state.service';
import {ObjectUtils} from '../../../../core/utils/object.utils';
import {ApplicationStateService} from '../../../services/application-state.service';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {AztecScannerLibDataModel} from '../../../models/aztec-scanner-library.model';
import {TranslateService} from '@ngx-translate/core';
import {MetricsService} from '../../../services/metrics.service';
import {CustomMetricsName} from '../../models/metrics/custom-metrics-name.enum';
import {MetricsType} from '../../models/metrics/metrics-type.enum';
import {SectionsStateService} from '../../../services/sections-state.service';
import {BusinessParamName} from '../../../models/request-offer/request-offer.enum';
import {AssetsService} from '../../../services/assets.service';
import {UrlParamsService} from '../../../../core/services/impl/url-params.service';
import {QueryParamName} from '../../../../core/enums/query-param-name.enum';
import {RuntimeEnvironment} from '../../../enums/runtime-environment.enum';

declare var Neptis: any;

@Component({
  selector: 'app-aztec-scanner-dialog',
  templateUrl: './aztec-scanner-dialog.component.html',
  styleUrls: ['./aztec-scanner-dialog.component.scss']
})
export class AztecScannerDialogComponent implements OnInit, AfterViewInit {

  @ViewChild('dynamicNotificationContainer', { read: ViewContainerRef }) dynamicNotificationContainer: ViewContainerRef;
  @ViewChild('cameraContent') cameraContent: TemplateRef<any>;
  @ViewChild('mainView') mainView: TemplateRef<any>;

  public decoder: any;
  public pendingRequest = false;
  public instance = this;
  public cameraErrorMode = false;
  public waitingForDataMode = false;
  public cameraViewInitialMode = false;
  public loadingLib = false;
  public mainViewHeaderInformationText: string;

  readonly API_KEY: string = '2dd6a017-aa16-4473-b8db-1c41f1ffa6d9';
  readonly VIDEO_CONTAINER_id = 'cameraContainer';
  readonly DOWNLOAD_VEHICLE_HISTORY_FROM_SERVER = false;
  readonly WAITING_FOR_DATA_VIEW_TIME: number = 2500;
  readonly AZTEC_DECODER_LIB_URL = 'https://aztec.neptis.pl/js/aztec-decoder.js';
  public TRANSLATION_KEY = 'AZTEC_SCANNER_DIALOG_COMPONENT';
  private skipFirstStep: boolean;
  private consoleLogsEnabled = false;

  constructor(public stateService: ApplicationStateService,
              public calculationService: CalculationService,
              public aztecScannerService: AztecScannerService,
              public vehicleModelStateService: VehicleModelStateService,
              public assetsService: AssetsService,
              @Inject(MAT_DIALOG_DATA) public data: any,
              public translationService: TranslateService,
              public metricsService: MetricsService,
              private urlParamsService: UrlParamsService,
              public sectionStateService: SectionsStateService,
              public dialogRef: MatDialogRef<AztecScannerDialogComponent>) {
  }

  get logoUrl(): string {
    return logoUrl();
  }

  async ngOnInit() {
    await this.loadAztecDecoderLibrary();
    this.skipFirstStep = (this.data && this.data.hasOwnProperty('skipFirstStep')) ? this.data.skipFirstStep : false;
    if (this.skipFirstStep) {
      this.createScanningView();
      setTimeout(() => this.initCamera());
    } else {
      this.createMainView();
    }
  }

  public ngAfterViewInit(): void {
    this.mainViewHeaderInformationText = this.translationService.instant(this.TRANSLATION_KEY + '.mainViewInformationText');
  }

  public startScanning(): void {
    this.sendUserDecisionMetric(AztecScannerUserDecision.SCAN);
    this.createScanningView();
    setTimeout(() => this.initCamera());
  }

  public stopScanning(): void {
    if (!this.waitingForDataMode) {
      this.sendUserDecisionMetric(AztecScannerUserDecision.CANCEL_SCANNING);
    }
    try {
      this.decoder.stop();
    } catch (e) {
      console.log(e);
    }
    this.closeScanningView();

    if (this.skipFirstStep) {
      this.closeDialog();
    }
  }

  public closeDialog(): void {
    if (!this.waitingForDataMode) {
      this.sendUserDecisionMetric(AztecScannerUserDecision.WRITE_DATA);
    }
    this.dialogRef.close();
  }

  protected createScanningView(): void {
    this.cameraViewInitialMode = true;
    this.dynamicNotificationContainer.clear();
    this.dynamicNotificationContainer.createEmbeddedView(this.cameraContent);
  }

  protected closeScanningView(): void {
    this.cameraViewInitialMode = false;
    this.dynamicNotificationContainer.clear();
    this.dynamicNotificationContainer.createEmbeddedView(this.mainView);
  }

  protected createMainView(): void {
    this.dynamicNotificationContainer.createEmbeddedView(this.mainView);
  }

  protected initCamera(): void {
    try {
      this.decoder = new Neptis.RegistrationCertificateDecoder(
        this.API_KEY,
        this.VIDEO_CONTAINER_id,
        this.DOWNLOAD_VEHICLE_HISTORY_FROM_SERVER,
        (instance) => this.onVideoInitializedSuccessHandler(),
        (data) => this.onVideoInitializedErrorHandler(data),
        (instance) => this.onScanSuccessHandler(),
        (data) => this.onDecodedSuccessHandler(data),
        (message) => this.onDecodedErrorHandler(message)
      );

      this.decoder.start();
      this.pendingRequest = false;

    } catch (e) {
      this.sendCameraInitErrorMetric(e);
      this.cameraErrorMode = true;
      this.cameraViewInitialMode = false;
      this.mainViewHeaderInformationText = '';
      this.dynamicNotificationContainer.clear();
      this.dynamicNotificationContainer.createEmbeddedView(this.mainView);
    }
  }

  protected onDecodedSuccessHandler(aztecData: AztecScannerLibDataModel): void {
    // success data from library server and send data to backend
    this.log('DecodedSuccess', aztecData);
    this.sendNeptisCallbackMetric('DecodedSuccess', aztecData);
    this.sendAztecFromLibraryResponse(aztecData);
    if (!this.pendingRequest) {
      this.aztecScannerService.sendDecodedAztec(aztecData.aztecEncoded)
        .then(data => {
          this.waitingForDataMode = true;
          this.mainViewHeaderInformationText = this.translationService.instant(this.TRANSLATION_KEY + '.processingData');
          this.stopScanning();

          setTimeout(() => {
            // TEST CASE
            this.sectionStateService.prepareFirstStepAfterAztecData();

            this.stateService.handleRegistrationDataFromAztec(data);
            this.handleCalculationData(data);

            this.vehicleModelStateService.init();
            this.closeDialog();
          }, this.WAITING_FOR_DATA_VIEW_TIME)
        });
    }
    this.pendingRequest = true;
  };

  protected onVideoInitializedSuccessHandler(): void {
    // Camera initialized, waiting for code scanning
    this.log('VideoInitializedSuccess');
    this.sendNeptisCallbackMetric('VideoInitializedSuccess');
  };

  protected onScanSuccessHandler(): void {
    // Success aztec decoded with library
    this.waitingForDataMode = true;
    this.log('ScanSuccess');
    this.sendNeptisCallbackMetric('ScanSuccess');
  };

  protected onVideoInitializedErrorHandler(data: any): void {
    this.log('VideoInitializedError', data);
    this.sendNeptisCallbackMetric('VideoInitializedError', data);
  };

  protected onDecodedErrorHandler(message): void {
    this.log('DecodedError', message);
    this.sendNeptisCallbackMetric('DecodedError', message);
  };

  protected handleCalculationData(data: any): void {
    if (data.hasOwnProperty('calculationData')) {
      this.stateService.state.dataFromAztec = true;
      this.stateService.state.usedPreviewMode = true;
      const calculationData = this.mergeWithExistingCalculationData(data.calculationData);
      this.calculationService.dataFromSession.emit(calculationData);

      const sourceFromUrl = this.urlParamsService.getQueryParam(QueryParamName.SOURCE);
      if (!isNotNullOrUndefined(sourceFromUrl)) {
        this.stateService.saveBusinessParameter(BusinessParamName.CLIENT_SOURCE, 'aztec');
      }
    }
  }

  protected mergeWithExistingCalculationData(calculationData: any): any {
    return ObjectUtils.mergeDeep(this.calculationService.calculationData, calculationData);
  }

  protected sendNeptisCallbackMetric(callback: string, callbackData?: any): void {
    const data: any = {
      callback: callback
    };
    if (isNotNullOrUndefined(callbackData)) {
      data.callbackData = callbackData
    }
    this.metricsService.sendSimpleMetric(CustomMetricsName.AZTEC_SCANNER_NEPTIS_CALLBACK, MetricsType.PROCESS, JSON.stringify(data));
  }

  protected log(message: string, data?: any): void {
    if (this.consoleLogsEnabled) {
      console.log(`Neptis:${message}`, data);
    }
  }

  protected sendUserDecisionMetric(decision: AztecScannerUserDecision): void {
    this.metricsService.sendSimpleMetric(CustomMetricsName.AZTEC_SCANNER_DECISION, MetricsType.BUTTON, decision);
  }

  protected sendAztecFromLibraryResponse(response: any): void {
    this.metricsService.sendSimpleMetric(CustomMetricsName.AZTEC_LIBRARY_RESPONSE, MetricsType.RESPONSE, response);
  }

  protected sendCameraInitErrorMetric(error: any): void {
    this.metricsService.sendSimpleMetric(CustomMetricsName.CAMERA_INIT_ERROR, MetricsType.PROCESS, JSON.stringify(error));
  }

  private async loadAztecDecoderLibrary() {
    return new Promise(res => {
      if (!this.stateService.aztecDecoderLibInitialized) {
        this.loadingLib = true;
        const script = document.createElement('script');
        script.onload = () => {
          this.metricsService.sendSimpleMetric(CustomMetricsName.LOADING_SCRIPT_SUCCESS, MetricsType.PROCESS, this.AZTEC_DECODER_LIB_URL);
          this.stateService.aztecDecoderLibInitialized = true;
          this.loadingLib = false;
          res();
        };
        script.onerror = () => {
          this.metricsService.sendSimpleMetric(CustomMetricsName.LOADING_SCRIPT_ERROR, MetricsType.PROCESS, this.AZTEC_DECODER_LIB_URL);
        }
        script.src = this.AZTEC_DECODER_LIB_URL;
        document.head.appendChild(script);
      } else {
        res();
      }
    });
  }

}

export enum AztecScannerUserDecision {
  SCAN = 'scan',
  WRITE_DATA = 'writeData',
  CANCEL_SCANNING = 'cancelScanning',
}
